/**
 * 締めの画面 /closing（#267 3a、docs/97 決めごと8、Figma「締め請求書と分割払い（10/28）」① 274:54）。一覧の型（ListHead）。
 *   題の行：‹ 請求書一覧｜締め請求書（？）｜月の切替（‹ 2026年10月分 ›）
 *   表のカード：タブ（すべて／未作成／下書き／発行済）・絞り込み（{customer}）・選んだときの帯（n社 選択・合計（税抜）・選択解除・まとめて作成）
 *   行＝{customer}：締め日・期間・件数（前の期間の出し忘れ）・金額（税抜）・状態・「下書き作成」か請求書へのリンク・開閉
 *   行を開く（未作成だけ）＝対象の書類（納品日・書類・番号・件名・{project}・金額・「外す ⌄」＝次の締めに回す（保存しない）／別請求にする）と 明細の形（全行｜1枚1行）
 * 左のメニューに項目は足さない（入口はホームの「締め n社」と請求書の一覧。印は請求書＝AreaIcon の areaOfPath）
 */
import {
  BUNDLE_MODE_LABEL,
  CLOSING_MSG,
  CLOSING_STATE_LABEL,
  closingDayLabel,
  closingMonthLabel,
  shiftMonth,
  type ClosingCreateResult,
  type ClosingMode,
  type ClosingOverview,
  type ClosingRow,
  type ClosingState,
  type ClosingTargets,
} from "@genbaryoku/contract";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { BackButton, usePageName } from "../../components/BackButton.jsx";
import { FilterInput, ListHead } from "../../components/ListHead.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { RowMenu } from "../../components/RowMenu.jsx";
import { Segmented } from "../../components/Segmented.jsx";
import { Icon } from "../../components/Shell.jsx";
import { ErrorText, Help, useToast } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { mdSlash, yen } from "../../lib/num.js";

type Tab = "all" | ClosingState;
const TABS: Array<[Tab, string]> = [
  ["all", "すべて"],
  ["none", CLOSING_STATE_LABEL.none],
  ["draft", CLOSING_STATE_LABEL.draft],
  ["issued", CLOSING_STATE_LABEL.issued],
];
const STATE_CHIP: Record<ClosingState, string> = { none: "chip", draft: "chip quiet", issued: "chip ai" };
/** 期間の短い表示：10/1〜10/31 */
const periodShort = (a: string, b: string) => `${Number(a.slice(5, 7))}/${Number(a.slice(8, 10))}〜${Number(b.slice(5, 7))}/${Number(b.slice(8, 10))}`;

export function ClosingPage() {
  const L = useLabels();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const monthQ = sp.get("month") ?? "";
  usePageName(CLOSING_MSG.title);
  const ov = useQuery({
    queryKey: ["closing", monthQ],
    queryFn: () => api<ClosingOverview>("GET", `/closing${monthQ ? `?month=${monthQ}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const month = ov.data?.month ?? monthQ;
  const [tab, setTab] = useState<Tab>("all");
  const [q, setQ] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  /** 「次の締めに回す」にした書類（{customer}ごと。保存しない） */
  const [exclude, setExclude] = useState<Record<string, string[]>>({});
  const [mode, setMode] = useState<ClosingMode | null>(null);
  const [busy, setBusy] = useState(false);
  const { toast, node: toastNode } = useToast();
  const modeNow: ClosingMode = mode ?? ov.data?.defaultMode ?? "perNote";

  const setMonth = (m: string) => {
    setPicked([]);
    setOpen(null);
    setExclude({});
    setSp((p) => {
      const n = new URLSearchParams(p);
      n.set("month", m);
      return n;
    });
  };
  const rows = ov.data?.rows ?? [];
  const word = q.trim();
  const pick = (k: Tab) => rows.filter((r) => (k === "all" || r.state === k) && (!word || r.client.name.includes(word)));
  const items = pick(tab);
  const pickedRows = rows.filter((r) => picked.includes(r.client.id));

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["closing"] });
    void qc.invalidateQueries({ queryKey: ["closing-targets"] });
    void qc.invalidateQueries({ queryKey: ["invoices"] });
    void qc.invalidateQueries({ queryKey: ["home"] });
  };
  const create = async (ids: string[]) => {
    if (busy || !ids.length || !month) return;
    setBusy(true);
    try {
      const r = await api<ClosingCreateResult>("POST", "/closing/create", { month, mode: modeNow, clients: ids.map((id) => ({ clientId: id, exclude: exclude[id] ?? [] })) });
      refresh();
      setPicked([]);
      for (const f of r.failed) toast(`${f.clientName}：${f.message}`, undefined, "error", "closing-failed");
      if (r.created.length === 1 && ids.length === 1) {
        nav(`/invoices/${r.created[0]!.invoiceId}`, { state: { notice: CLOSING_MSG.createdOne } });
        return;
      }
      if (r.created.length) toast(CLOSING_MSG.created(r.created.length), undefined, "success", "closing-created");
    } catch (e) {
      toast(messageOf(e), undefined, "error");
    } finally {
      setBusy(false);
    }
  };

  const search = <FilterInput value={q} onChange={setQ} placeholder={t(`{customer}で絞り込み`)} testId="closing-search" />;
  const selectable = (r: ClosingRow) => r.state === "none" && r.count > 0;
  const cols = 9;
  return (
    <ListHead
      back={<BackButton fallback={{ to: "/invoices", label: CLOSING_MSG.back }} />}
      title={
        <>
          {CLOSING_MSG.title}
          <Help testId="help-closing" label={CLOSING_MSG.title} text={t(CLOSING_MSG.help)} />
        </>
      }
      titleTestId="closing-title"
      extra={
        month ? (
          <span className="cl-month" role="group" aria-label="月の切替" data-testid="closing-month">
            <button type="button" className="btn sm quiet" aria-label="前の月" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="closing-month-prev">
              ‹
            </button>
            <b className="num" data-testid="closing-month-label">{closingMonthLabel(month)}</b>
            <button type="button" className="btn sm quiet" aria-label="次の月" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="closing-month-next">
              ›
            </button>
          </span>
        ) : null
      }
    >
      {ov.isError ? <ErrorText>{messageOf(ov.error)}</ErrorText> : null}
      <div className="lcard">
        <ListTabs id="cl" label="締めを状態で絞る" tabs={TABS.map(([k, label]) => ({ key: k, label, count: ov.data ? pick(k).length : "…" }))} value={tab} onChange={(k) => setTab(k as Tab)} filter={search} />
        <div className="tbl-wrap ltab-panel" {...listTabPanel("cl", tab)}>
          {pickedRows.length ? (
            <div className="cl-bulk" data-testid="closing-bulk">
              <b>{CLOSING_MSG.selected(pickedRows.length)}</b>
              <span>
                合計（税抜） <b className="num">{yen(pickedRows.reduce((a, r) => a + r.subtotal, 0))}</b> 円
              </span>
              <span className="sp" />
              <button type="button" className="btn text" onClick={() => setPicked([])} data-testid="closing-deselect">
                {CLOSING_MSG.deselect}
              </button>
              <button type="button" className="btn primary" disabled={busy} onClick={() => void create(pickedRows.map((r) => r.client.id))} data-testid="closing-create-all">
                {CLOSING_MSG.createAll}
              </button>
            </div>
          ) : null}
          <table className="list cl-list" data-testid="closing-list">
            <thead>
              <tr>
                <th className="pick" aria-label="選択" />
                <th>{t(CLOSING_MSG.colClient)}</th>
                <th>{CLOSING_MSG.colClosingDay}</th>
                <th>{CLOSING_MSG.colPeriod}</th>
                <th className="amt">{CLOSING_MSG.colCount}</th>
                <th className="amt">{CLOSING_MSG.colAmount}</th>
                <th>{CLOSING_MSG.colState}</th>
                <th />
                <th className="more" aria-label="開閉" />
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const isOpen = open === r.client.id && r.state === "none";
                const inv = r.invoice;
                return (
                  <Fragment key={r.client.id}>
                    <tr className={"r" + (isOpen ? " is-open" : "")} data-testid="closing-row" data-client={r.client.name} onClick={() => r.state === "none" && setOpen(isOpen ? null : r.client.id)}>
                      <td className="pick" onClick={(e) => e.stopPropagation()}>
                        {selectable(r) ? (
                          <input
                            type="checkbox"
                            aria-label={`${r.client.name} を選ぶ`}
                            checked={picked.includes(r.client.id)}
                            onChange={(e) => setPicked((p) => (e.target.checked ? [...p, r.client.id] : p.filter((y) => y !== r.client.id)))}
                            data-testid={`closing-pick-${r.client.name}`}
                          />
                        ) : null}
                      </td>
                      <td>
                        <b>{r.client.name}</b>
                        {r.taxOn === "delivery" ? <div className="muted sm">{t(CLOSING_MSG.taxOnDelivery)}</div> : null}
                      </td>
                      <td>{closingDayLabel(r.closingDay)}</td>
                      <td className="num">{periodShort(r.periodStart, r.periodEnd)}</td>
                      <td className="amt num">
                        {inv ? `${inv.count}件` : `${r.count}件`}
                        {!inv && r.prevCount ? <div className="muted sm" data-testid="closing-prev-count">{CLOSING_MSG.prevCount(r.prevCount)}</div> : null}
                      </td>
                      <td className="amt num">
                        <b>{yen(inv ? inv.subtotal : r.subtotal)}</b>
                      </td>
                      <td>
                        <span className={STATE_CHIP[r.state]} data-testid="closing-state">{CLOSING_STATE_LABEL[r.state]}</span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {inv ? (
                          <Link to={`/invoices/${inv.id}`} data-testid="closing-invoice">{inv.documentNo ?? "請求書"} ›</Link>
                        ) : r.count > 0 ? (
                          <button type="button" className="btn tonal" disabled={busy} onClick={() => void create([r.client.id])} data-testid={`closing-create-${r.client.name}`}>
                            {CLOSING_MSG.create}
                          </button>
                        ) : null}
                      </td>
                      <td className="more">
                        {r.state === "none" ? (
                          <button type="button" className="rowmore" aria-expanded={isOpen} aria-label={`${r.client.name} の対象`} onClick={(e) => (e.stopPropagation(), setOpen(isOpen ? null : r.client.id))} data-testid="closing-toggle">
                            <Icon k="chev" />
                          </button>
                        ) : null}
                      </td>
                    </tr>
                    {isOpen && month ? (
                      <tr className="cl-open">
                        <td colSpan={cols}>
                          <Targets
                            month={month}
                            row={r}
                            excluded={exclude[r.client.id] ?? []}
                            onExclude={(id, on) => setExclude((x) => ({ ...x, [r.client.id]: on ? [...(x[r.client.id] ?? []), id] : (x[r.client.id] ?? []).filter((y) => y !== id) }))}
                            onSeparated={() => {
                              refresh();
                              toast(CLOSING_MSG.separated, undefined, "success", "closing-separated");
                            }}
                            onError={(m) => toast(m, undefined, "error")}
                            mode={modeNow}
                            onMode={setMode}
                            groupWord={L.project}
                          />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
              {ov.data && !items.length ? (
                <tr className="emptyrows">
                  <td colSpan={cols} data-testid="closing-empty">
                    {CLOSING_MSG.empty}
                    <div className="muted sm">{t(CLOSING_MSG.emptyHelp)}</div>
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
          {rows.length ? (
            <div className="pul-sum" data-testid="closing-sum">
              <span>{CLOSING_MSG.footer(rows.length, rows.reduce((a, r) => a + (r.invoice ? r.invoice.count : r.count), 0))}</span>
              <span>
                合計（税抜） <b className="num">{yen(rows.reduce((a, r) => a + (r.invoice ? r.invoice.subtotal : r.subtotal), 0))}</b> 円
              </span>
            </div>
          ) : null}
        </div>
      </div>
      {toastNode}
    </ListHead>
  );
}

/** 開いた行：対象の書類と「外す」・明細の形 */
function Targets({
  month,
  row,
  excluded,
  onExclude,
  onSeparated,
  onError,
  mode,
  onMode,
  groupWord,
}: {
  month: string;
  row: ClosingRow;
  excluded: string[];
  onExclude: (id: string, on: boolean) => void;
  onSeparated: () => void;
  onError: (m: string) => void;
  mode: ClosingMode;
  onMode: (m: ClosingMode) => void;
  groupWord: string;
}) {
  const q = useQuery({ queryKey: ["closing-targets", row.client.id, month], queryFn: () => api<ClosingTargets>("GET", `/closing/${row.client.id}?month=${month}`) });
  const items = q.data?.items ?? [];
  const nextMonth = Number(shiftMonth(month, 1).slice(5, 7));
  const kept = items.filter((x) => !excluded.includes(x.id));
  return (
    <div className="cl-panel" data-testid="closing-targets">
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      <table className="list cl-sub">
        <thead>
          <tr>
            <th>{CLOSING_MSG.colDate}</th>
            <th>{CLOSING_MSG.colKind}</th>
            <th>{CLOSING_MSG.colNo}</th>
            <th>{CLOSING_MSG.colSubject}</th>
            <th>{groupWord}</th>
            <th className="amt">{CLOSING_MSG.colAmount}</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((x) => {
            const ex = excluded.includes(x.id);
            return (
              <tr key={x.id} className={ex ? "is-excluded" : undefined} data-testid="closing-target" data-no={x.documentNo ?? ""}>
                <td className="num">{mdSlash(x.issueDate)}</td>
                <td>{x.kind === "acceptance" ? t("{acceptance}") : t("{deliveryNote}")}</td>
                <td className="num">
                  <Link to={x.kind === "acceptance" ? `/acceptances/${x.id}` : `/delivery-notes/${x.id}`}>{x.documentNo ?? "—"} ›</Link>
                  {x.prev ? <span className="chip quiet cl-prev" data-testid="closing-prev">{CLOSING_MSG.prev}</span> : null}
                  {ex ? <span className="chip quiet cl-prev" data-testid="closing-next-chip">{CLOSING_MSG.nextClosing}</span> : null}
                </td>
                <td>{x.subject || <span className="muted">—</span>}</td>
                <td className="muted">{x.groupName ?? "—"}</td>
                <td className="amt num">{yen(x.subtotal)}</td>
                <td className="more">
                  <RowMenu
                    label={`${x.documentNo ?? ""} ${CLOSING_MSG.exclude}`}
                    trigger={
                      <>
                        {CLOSING_MSG.exclude}
                        <Icon k="chev" />
                      </>
                    }
                    triggerClass="btn text sm cl-exclude"
                    testId={`closing-exclude-${x.documentNo}`}
                    items={
                      ex
                        ? [{ label: CLOSING_MSG.undoExclude, onSelect: () => onExclude(x.id, false), testId: "closing-undo-exclude" }]
                        : [
                            { label: CLOSING_MSG.nextClosing, note: CLOSING_MSG.nextClosingNote(nextMonth), onSelect: () => onExclude(x.id, true), testId: "closing-next" },
                            {
                              label: CLOSING_MSG.separate,
                              note: CLOSING_MSG.separateNote,
                              testId: "closing-separate",
                              onSelect: () =>
                                void api<{ ok: true }>("POST", "/closing/separate", { documentId: x.id })
                                  .then(onSeparated)
                                  .catch((e) => onError(messageOf(e))),
                            },
                          ]
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="cl-foot">
        <span className="muted">{CLOSING_MSG.targets(kept.length)}</span>
        <span>
          合計（税抜） <b className="num">{yen(kept.reduce((a, x) => a + x.subtotal, 0))}</b> 円
        </span>
        <span className="sp" />
        <span className="muted" id="cl-mode-l">{CLOSING_MSG.mode}</span>
        <Segmented labelledBy="cl-mode-l" options={(["lines", "perNote"] as const).map((v) => ({ value: v, label: BUNDLE_MODE_LABEL[v], testId: `closing-mode-${v}` }))} value={mode} onChange={onMode} />
      </div>
    </div>
  );
}
